import { useMemo, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '@/app/store/store';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { isGoldCoinSearchQuery } from '@/features/search/utils/searchNavigation';
import { plpBuild } from '@/features/plp/config/plp.build';
import { filtersBuild } from '@/features/filters/config/filters.build';
import { usePlpFilters } from '@/features/filters/presentation/hooks/usePlpFilters';
import { usePlpDesktopFilters } from '@/features/filters/presentation/hooks/usePlpDesktopFilters';
import { useVideoCallCart } from '@/features/video-call/presentation/hooks/useVideoCallCart';
import type { ListingPageResult } from '@/features/plp/domain/models/ProductListing';
import type { GetFiltersParams } from '@/features/filters/application/services/FiltersService';
import {
  getPlpListingKey,
  getPlpBrowseTagsForApi,
  getPlpApiQueryContextFromSearch,
  getPreferredProductsFromSearch,
} from '@/features/plp/utils/plpUrl';
import { PLP_PAGE_TYPE, type PlpSortByValue } from '@/features/plp/constants';
import {
  getSearchParamsFromLocation,
  getSidFromSearch,
  getSortByFromSearch,
} from '@/pages/product-listing/searchPlpUtils';
import { SearchPlpPageView } from '@/pages/product-listing/SearchPlpPageView';
import { useSearchPlpShell } from '@/pages/product-listing/useSearchPlpShell';
import { PlpSortSheet } from '@/features/plp/presentation/components/PlpSortSheet';
import { PlpFilterSheet } from '@/features/filters/presentation/components/PlpFilterSheet';
import { PlpDesktopFilterSidebar } from '@/features/filters/presentation/components/PlpDesktopFilterSidebar';
import { VideoCallStickyBar } from '@/features/video-call/presentation/components/VideoCallStickyBar';

export default function SearchPlpPage() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const location = useLocation();
  const pathname = location.pathname || '/search';

  const sidFromUrl = useMemo(() => getSidFromSearch(location.search), [location.search]);
  const sortBy = useMemo(() => getSortByFromSearch(location.search), [location.search]);
  const preferredProducts = useMemo(() => getPreferredProductsFromSearch(location.search), [location.search]);
  const apiQueryContext = useMemo(() => getPlpApiQueryContextFromSearch(location.search), [location.search]);
  const isFiltered = pathname.includes('/filter/');

  const searchUrlTags = useMemo(
    () => new URLSearchParams(location.search).get('tags')?.trim() ?? '',
    [location.search],
  );

  const tags = useMemo(() => getPlpBrowseTagsForApi(pathname), [pathname]);
  const searchParams = useMemo(() => getSearchParamsFromLocation(location.search), [location.search]);

  const { service } = useMemo(() => plpBuild(dispatch), [dispatch]);
  const { filtersService } = useMemo(() => filtersBuild(dispatch), [dispatch]);

  const [isSortSheetOpen, setIsSortSheetOpen] = useState(false);

  useEffect(() => {
    const searchQuery = searchParams.search_query;
    if (!searchQuery || !isGoldCoinSearchQuery(searchQuery)) return;
    if (navigationType === 'POP') return;
    window.location.replace(window.location.href);
  }, [searchParams.search_query, navigationType]);

  const plpKey = useMemo(() => getPlpListingKey(pathname, location.search), [pathname, location.search]);
  const listingSid = sidFromUrl ?? apiQueryContext.sid;
  const listingPageType = PLP_PAGE_TYPE.SEARCH;
  const listingDesignIds = apiQueryContext.design_ids;

  const fetchPage = useCallback(
    async (pageNumber: number, opts?: { signal?: AbortSignal }): Promise<ListingPageResult> => {
      const result = await service.getListing({
        pageNumber,
        tags: searchUrlTags,
        tags_to_sort_filters: tags,
        sortBy,
        page_type: listingPageType,
        ...(preferredProducts ? { preferredProducts } : {}),
        is_filtered: isFiltered,
        sid: listingSid,
        storeIds: apiQueryContext.storeIds,
        wid: apiQueryContext.wid,
        url_query_string: apiQueryContext.url_query_string,
        design_ids: listingDesignIds,
        ...searchParams,
        ...(opts?.signal ? { signal: opts.signal } : {}),
      });
      return result as unknown as ListingPageResult;
    },
    [
      service,
      searchUrlTags,
      tags,
      sortBy,
      listingPageType,
      preferredProducts,
      isFiltered,
      listingSid,
      apiQueryContext,
      listingDesignIds,
      searchParams,
    ],
  );

  const shell = useSearchPlpShell({
    pathname,
    listingKey: plpKey,
    fetchPage,
    tags,
    pageType: PLP_PAGE_TYPE.SEARCH,
    sortBy,
  });

  const { plp, plpAnalytics } = shell;

  const filterParams = useMemo(
    (): Omit<GetFiltersParams, 'tags'> => ({
      page_type: listingPageType,
      tags_to_sort_filters: tags,
      is_filtered: isFiltered,
      is_for_filter_change: false,
      sid: listingSid,
      storeIds: apiQueryContext.storeIds,
      wid: apiQueryContext.wid,
      url_query_string: apiQueryContext.url_query_string,
      design_ids: listingDesignIds,
      search_query: searchParams.search_query,
    }),
    [listingPageType, tags, isFiltered, listingSid, apiQueryContext, listingDesignIds, searchParams],
  );

  const {
    isOpen: isFilterSheetOpen,
    openFilterSheet,
    previewFilters,
    selectedOptions: filterSelectedOptions,
    applyUrl: filterApplyUrl,
    isLoadingPreview,
    toggleOption: handleToggleFilterOption,
    handleApply: handleApplyFilter,
    handleReset: handleResetFilter,
    handleClose: handleCloseFilter,
  } = usePlpFilters({
    listingFilters: plp.listingMeta?.filters ?? [],
    filtersResetUrl: plp.listingMeta?.filtersResetUrl,
    tags,
    filterParams,
    service: filtersService,
    navigate,
  });

  const {
    previewFilters: desktopPreviewFilters,
    selectedOptions: desktopSelectedOptions,
    isPendingNavigation: isDesktopFilterPending,
    toggleOption: handleDesktopToggleFilter,
    handleReset: handleDesktopResetFilter,
  } = usePlpDesktopFilters({
    listingFilters: plp.listingMeta?.filters ?? [],
    filtersResetUrl: plp.listingMeta?.filtersResetUrl,
    isLoading: plp.isLoading,
    tags,
    filterParams,
    service: filtersService,
    navigate,
  });

  const { videoCallTahItemsCount, maxVideoCallCount, message: videoCallMessage } = useVideoCallCart();

  const applySortToUrl = useCallback(
    (nextSort: PlpSortByValue) => {
      const urlParams = new URLSearchParams(location.search);
      urlParams.set('sortby', nextSort);
      navigate({ pathname: location.pathname, search: `?${urlParams.toString()}` }, { replace: false });
    },
    [location.pathname, location.search, navigate],
  );

  const handleOpenSortSheet = useCallback(() => {
    plpAnalytics.trackSortFilterClicked();
    setIsSortSheetOpen(true);
  }, [plpAnalytics]);

  const handleApplySortFromSheet = useCallback(
    (nextSort: PlpSortByValue) => {
      plpAnalytics.trackSortApply(nextSort);
      applySortToUrl(nextSort);
      setIsSortSheetOpen(false);
      window.scrollTo({ top: 0, behavior: 'auto' });
    },
    [applySortToUrl, plpAnalytics],
  );

  const handleApplySort = useCallback(
    (nextSort: PlpSortByValue) => {
      plpAnalytics.trackSortApply(nextSort);
      applySortToUrl(nextSort);
      window.scrollTo({ top: 0, behavior: 'auto' });
    },
    [applySortToUrl, plpAnalytics],
  );

  const handleOpenFilter = useCallback(() => {
    plpAnalytics.trackFilterButtonClicked();
    openFilterSheet();
  }, [plpAnalytics, openFilterSheet]);

  const handleApplyFilterWithAnalytics = useCallback(() => {
    plpAnalytics.trackFilterApply(previewFilters, filterSelectedOptions);
    handleApplyFilter();
  }, [plpAnalytics, previewFilters, filterSelectedOptions, handleApplyFilter]);

  const handleResetFilterWithAnalytics = useCallback(() => {
    plpAnalytics.trackFilterReset(previewFilters, filterSelectedOptions);
    handleResetFilter();
  }, [plpAnalytics, previewFilters, filterSelectedOptions, handleResetFilter]);

  const handleCloseFilterWithAnalytics = useCallback(() => {
    plpAnalytics.trackFilterClose();
    handleCloseFilter();
  }, [plpAnalytics, handleCloseFilter]);

  const handleDesktopResetWithAnalytics = useCallback(() => {
    plpAnalytics.trackFilterReset(desktopPreviewFilters, desktopSelectedOptions);
    handleDesktopResetFilter();
  }, [plpAnalytics, desktopPreviewFilters, desktopSelectedOptions, handleDesktopResetFilter]);

  const desktopFilterData = {
    shouldShow: plp.isLoading || desktopPreviewFilters.length > 0,
    filters: desktopPreviewFilters,
    selectedOptions: desktopSelectedOptions,
    filtersResetUrl: plp.listingMeta?.filtersResetUrl,
    isPendingNavigation: isDesktopFilterPending,
    isStorePlp: false as const,
    currentSortBy: sortBy,
    onToggleOption: handleDesktopToggleFilter,
    onReset: handleDesktopResetWithAnalytics,
    onFilterItemToggle: plpAnalytics.trackDesktopFilterItemSelected,
  };

  return (
    <SearchPlpPageView
      shell={shell}
      sortBy={sortBy}
      plpBaseOverrides={{
        onOpenSort: handleOpenSortSheet,
        onOpenFilter: handleOpenFilter,
        onApplySort: handleApplySort,
        onSortHover: plpAnalytics.trackSortFilterHovered,
        isDesktopPendingNavigation: isDesktopFilterPending,
      }}
      renderDesktopFilterSidebar={
        desktopFilterData.shouldShow
          ? () => (
              <PlpDesktopFilterSidebar
                filters={desktopFilterData.filters}
                selectedOptions={desktopFilterData.selectedOptions}
                filtersResetUrl={desktopFilterData.filtersResetUrl}
                isPendingNavigation={desktopFilterData.isPendingNavigation}
                isStorePlp={desktopFilterData.isStorePlp}
                currentSortBy={desktopFilterData.currentSortBy}
                onToggleOption={desktopFilterData.onToggleOption}
                onReset={desktopFilterData.onReset}
                onFilterItemToggle={desktopFilterData.onFilterItemToggle}
              />
            )
          : undefined
      }
    >
      <PlpFilterSheet
        open={isFilterSheetOpen}
        filters={previewFilters}
        selectedOptions={filterSelectedOptions}
        applyUrl={filterApplyUrl}
        isLoadingPreview={isLoadingPreview}
        filtersResetUrl={plp.listingMeta?.filtersResetUrl}
        onToggleOption={handleToggleFilterOption}
        onApply={handleApplyFilterWithAnalytics}
        onReset={handleResetFilterWithAnalytics}
        onClose={handleCloseFilterWithAnalytics}
        onFilterItemToggle={plpAnalytics.trackFilterItemSelected}
        onFilterCategoryExpand={plpAnalytics.trackFilterCategoryExpand}
      />
      <PlpSortSheet
        open={isSortSheetOpen}
        currentSortBy={sortBy}
        onOpenChange={setIsSortSheetOpen}
        onApply={handleApplySortFromSheet}
        onSortOptionSelect={plpAnalytics.trackSortOptionSelect}
        onCancel={plpAnalytics.trackSortCancel}
      />
      <VideoCallStickyBar
        itemsCount={videoCallTahItemsCount}
        maxCount={maxVideoCallCount}
        message={videoCallMessage}
      />
    </SearchPlpPageView>
  );
}
